import { Component } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export type Lang = 'en' | 'es';

@Component({
  selector: 'app-hardship',
  templateUrl: './hardship.component.html',
  styleUrls: ['./hardship.component.css']
})
export class HardshipComponent {

  isNavOpen = false;
  lang: Lang = 'en';

  setLang(l: Lang) {
    this.lang = l;
  }

  get t() {
    return this.translations[this.lang];
  }

  translations = {
    en: {
      nav: {
        login: 'LOGIN',
        register: 'REGISTER NOW',
      },
      hero: {
        tagline: 'FOR US. BY US. POWERED BY UMYO.',
        title: 'UP TO 20% BACK TO OUR COMMUNITY.',
        subtitle: 'WHEN OUR COMMUNITY GROWS, OUR ABILITY TO HELP GROWS.',
        desc: 'UMYO Español is about more than connecting people online. We are building a digital ecosystem designed to create opportunities, strengthen businesses, connect families, and provide meaningful assistance to participating members of our community.',
        pledge: 'As membership participation and ecosystem revenue grow, up to 20% can be allocated toward community hardship incentives and assistance programs.',
        tags: ['REGISTER', 'PARTICIPATE', 'CONNECT', 'GROW']
      },
      pledge: {
        label: 'WE PLEDGE UP TO',
        percent: '20%',
        suffix: 'OF REVENUE',
        text: 'WHEN OUR COMMUNITY GROWS, OUR ABILITY TO HELP GROWS. Together, we uplift, empower, and strengthen the communities we serve.'
      },
      incentives: {
        title: 'REAL SUPPORT FOR REAL LIFE.',
        subtitle: 'Sometimes a little help can make a big difference. From groceries and gasoline to electricity, transportation, housing, child care, family needs, health-related emergencies, and unexpected circumstances, our goal is to create opportunities for eligible participating members to receive assistance when resources are available.',
        items: [
          {
            title: 'GAS ASSISTANCE',
            desc: 'Helping participating members with transportation expenses for work, school, appointments, and everyday needs.',
            amount: 'UP TO $50'
          },
          {
            title: 'FOOD ASSISTANCE',
            desc: 'Grocery assistance designed to help eligible individuals and families with everyday food expenses.',
            amount: 'UP TO $200'
          },
          {
            title: 'VISA CARD ASSISTANCE',
            desc: 'Eligible participating members may have opportunities to receive prepaid assistance for qualifying everyday needs.',
            amount: 'UP TO $200'
          },
          {
            title: 'ELECTRICITY ASSISTANCE',
            desc: 'Assistance toward qualifying household electricity expenses.',
            amount: 'UP TO $150'
          },
          {
            title: 'CAR NOTE ASSISTANCE',
            desc: 'Helping eligible participating members with qualifying vehicle payment needs.',
            amount: 'UP TO $500'
          },
          {
            title: 'RENT OR MORTGAGE ASSISTANCE',
            desc: 'Housing assistance designed to help eligible members facing qualifying financial hardship.',
            amount: 'UP TO $1,000'
          },
          {
            title: 'ELDERLY ASSISTANCE',
            desc: 'Additional assistance opportunities designed to support qualifying elderly members and households.',
            amount: 'UP TO $500'
          },
          {
            title: 'MILITARY & VETERAN ASSISTANCE',
            desc: 'Support opportunities designed for qualifying military members, veterans, and their families.',
            amount: 'UP TO $500'
          },
          {
            title: 'SINGLE PARENT ASSISTANCE',
            desc: 'Assistance opportunities for qualifying single parent households experiencing financial hardship.',
            amount: 'UP TO $500'
          },
          {
            title: 'DAY CARE ASSISTANCE',
            desc: 'Helping qualifying families with eligible child care expenses.',
            amount: 'UP TO $400'
          },
          {
            title: 'EMERGENCY ASSISTANCE',
            desc: 'Support for qualifying unexpected circumstances where additional assistance can make a meaningful difference.',
            amount: 'UP TO $1,000'
          },
          {
            title: 'AND MORE',
            desc: 'As the UMYO Español ecosystem grows, additional community assistance categories and opportunities may be introduced.',
            amount: 'ONGOING'
          }
        ]
      },
      howItWorks: {
        title: 'HOW IT WORKS',
        steps: [
          {
            number: 1,
            title: 'JOIN THE COMMUNITY',
            desc: 'Register and become a participating member of the UMYO Español ecosystem.'
          },
          {
            number: 2,
            title: 'STAY ACTIVE',
            desc: 'Connect, participate, support businesses, invite others, and help strengthen the community.'
          },
          {
            number: 3,
            title: 'WE GROW TOGETHER',
            desc: 'As membership participation and ecosystem revenue increase, our ability to fund community assistance can increase.'
          },
          {
            number: 4,
            title: 'APPLY WHEN PROGRAMS ARE AVAILABLE',
            desc: 'Eligible participating members can apply for available hardship programs based on the requirements established for each program.'
          },
          {
            number: 5,
            title: 'COMMUNITY SUPPORT IS DISTRIBUTED',
            desc: 'Approved assistance is provided according to available funding, eligibility requirements, program rules, and applicable limits.'
          }
        ]
      },
      grow: {
        title: 'WHEN WE GROW, WE GIVE.',
        desc: 'Every registration helps us do more for those who need it most.'
      },
      back: {
        title: 'UP TO 20% BACK.',
        subtitle: 'OUR COMMUNITY HELPS BUILD THE ECOSYSTEM. THE ECOSYSTEM HELPS SUPPORT OUR COMMUNITY.',
        desc: 'We are building technology with a larger purpose. Our goal is to create an ecosystem where community participation can help generate resources that support our people, strengthen businesses, create opportunities, and make a real difference in everyday lives.',
        list: [
          'OUR MEMBERS BUILD THE COMMUNITY.',
          'OUR BUSINESSES HELP POWER THE ECONOMY.',
          'OUR PARTICIPATION HELPS CREATE VALUE.',
          'OUR GROWTH HELPS CREATE MORE OPPORTUNITIES TO GIVE BACK.'
        ]
      },
      impact: {
        title: 'THE MORE WE GROW, THE MORE IMPACT WE CAN CREATE.',
        items: [
          { title: 'MORE MEMBERS.', desc: 'More people connected within our digital community.' },
          { title: 'MORE BUSINESSES.', desc: 'More opportunities to support businesses and circulate economic activity.' },
          { title: 'MORE PARTICIPATION.', desc: 'A stronger and more engaged ecosystem.' },
          { title: 'MORE IMPACT.', desc: 'Greater potential to create programs and resources that benefit our community.' }
        ],
        tagline: 'CONNECT. SUPPORT. GROW. GIVE BACK.'
      },
      built: {
        title: 'BUILT FOR OUR COMMUNITY.',
        desc: 'This is not simply about creating another website or directory. We are building a digital ecosystem that connects people, businesses, creators, professionals, organizations, technology, resources, and opportunities within one growing community.',
        emphasis: 'FOR US. BY US.',
        closing: 'When our community participates, our community becomes stronger.'
      },
      disclaimer: {
        title: 'IMPORTANT PROGRAM INFORMATION',
        points: [
          'Hardship incentives are not guaranteed cash payments or guaranteed member benefits.',
          'Programs are subject to eligibility requirements, available funding, membership participation, program rules, verification requirements, geographic availability, and applicable laws and regulations.',
          'Amounts displayed represent maximum potential assistance amounts for qualifying programs. Registration or membership does not guarantee approval or receipt of assistance.',
          'Program categories, amounts, eligibility requirements, application periods, and availability may change as the ecosystem develops and grows.'
        ],
        link: 'VIEW ELIGIBILITY & PROGRAM DETAILS'
      },
      cta: {
        lead: 'YOUR MEMBERSHIP HELPS BUILD THE ECOSYSTEM. Every registration helps strengthen the UMYO Español digital ecosystem, create opportunities, support businesses, expand technology, and increase our potential to make a meaningful impact throughout our community.',
        title: 'THIS IS BIGGER THAN A DIRECTORY.',
        subtitle: 'WE ARE BUILDING TECHNOLOGY WITH PURPOSE.',
        cta: 'JOIN TODAY. CONNECT TODAY. GROW WITH US.',
        button: 'REGISTER NOW'
      },
      footer: {
        tagline: 'FOR US. BY US. POWERED BY UMYO.',
        quickLinks: 'QUICK LINKS',
        resources: 'RESOURCES',
        connect: 'LET\'S CONNECT',
        copyright: '© 2026 UMYO Español Directory. All Rights Reserved.'
      }
    },
    es: {
      nav: {
        login: 'INICIAR SESIÓN',
        register: 'REGISTRARSE AHORA',
      },
      hero: {
        tagline: 'PARA NOSOTROS. POR NOSOTROS. POTENCIADO POR UMYO.',
        title: 'HASTA UN 20% DE VUELTA A NUESTRA COMUNIDAD.',
        subtitle: 'CUANDO NUESTRA COMUNIDAD CRECE, NUESTRA CAPACIDAD DE AYUDAR CRECE.',
        desc: 'UMYO Español es más que conectar personas en línea. Estamos construyendo un ecosistema digital diseñado para crear oportunidades, fortalecer negocios, conectar familias y brindar asistencia significativa a los miembros participantes de nuestra comunidad.',
        pledge: 'A medida que la participación de los miembros y los ingresos del ecosistema crecen, hasta el 20% puede ser asignado a programas de incentivos por dificultades y asistencia comunitaria.',
        tags: ['REGISTRARSE', 'PARTICIPAR', 'CONECTAR', 'CRECER']
      },
      pledge: {
        label: 'COMPROMETEMOS HASTA',
        percent: '20%',
        suffix: 'DE INGRESOS',
        text: 'CUANDO NUESTRA COMUNIDAD CRECE, NUESTRA CAPACIDAD DE AYUDAR CRECE. Juntos, elevamos, empoderamos y fortalecemos a las comunidades que servimos.'
      },
      incentives: {
        title: 'APOYO REAL PARA LA VIDA REAL.',
        subtitle: 'A veces un poco de ayuda puede marcar una gran diferencia. Desde alimentos y gasolina hasta electricidad, transporte, vivienda, cuidado infantil, necesidades familiares, emergencias de salud y circunstancias inesperadas, nuestro objetivo es crear oportunidades para que los miembros participantes elegibles reciban asistencia cuando los recursos estén disponibles.',
        items: [
          {
            title: 'ASISTENCIA PARA GASOLINA',
            desc: 'Ayudando a los miembros participantes con gastos de transporte para trabajo, escuela, citas y necesidades diarias.',
            amount: 'HASTA $50'
          },
          {
            title: 'ASISTENCIA PARA ALIMENTOS',
            desc: 'Asistencia para comestibles diseñada para ayudar a individuos y familias elegibles con gastos diarios de alimentos.',
            amount: 'HASTA $200'
          },
          {
            title: 'ASISTENCIA CON TARJETA VISA',
            desc: 'Los miembros participantes elegibles pueden tener oportunidades de recibir asistencia prepaga para necesidades diarias calificadas.',
            amount: 'HASTA $200'
          },
          {
            title: 'ASISTENCIA PARA ELECTRICIDAD',
            desc: 'Asistencia para gastos de electricidad doméstica calificados.',
            amount: 'HASTA $150'
          },
          {
            title: 'ASISTENCIA PARA PAGO DE AUTO',
            desc: 'Ayudando a miembros participantes elegibles con necesidades de pago de vehículos calificados.',
            amount: 'HASTA $500'
          },
          {
            title: 'ASISTENCIA PARA ALQUILER O HIPOTECA',
            desc: 'Asistencia para vivienda diseñada para ayudar a miembros elegibles que enfrentan dificultades financieras calificadas.',
            amount: 'HASTA $1,000'
          },
          {
            title: 'ASISTENCIA PARA ANCIANOS',
            desc: 'Oportunidades de asistencia adicionales diseñadas para apoyar a miembros ancianos elegibles y sus hogares.',
            amount: 'HASTA $500'
          },
          {
            title: 'ASISTENCIA PARA MILITARES Y VETERANOS',
            desc: 'Oportunidades de apoyo diseñadas para miembros militares calificados, veteranos y sus familias.',
            amount: 'HASTA $500'
          },
          {
            title: 'ASISTENCIA PARA PADRES SOLTEROS',
            desc: 'Oportunidades de asistencia para hogares de padres solteros calificados que enfrentan dificultades financieras.',
            amount: 'HASTA $500'
          },
          {
            title: 'ASISTENCIA PARA CUIDADO INFANTIL',
            desc: 'Ayudando a familias calificadas con gastos de cuidado infantil elegibles.',
            amount: 'HASTA $400'
          },
          {
            title: 'ASISTENCIA DE EMERGENCIA',
            desc: 'Apoyo para circunstancias inesperadas calificadas donde la asistencia adicional puede marcar una diferencia significativa.',
            amount: 'HASTA $1,000'
          },
          {
            title: 'Y MÁS',
            desc: 'A medida que el ecosistema UMYO Español crece, se pueden introducir categorías y oportunidades de asistencia comunitaria adicionales.',
            amount: 'EN CURSO'
          }
        ]
      },
      howItWorks: {
        title: 'CÓMO FUNCIONA',
        steps: [
          {
            number: 1,
            title: 'ÚNETE A LA COMUNIDAD',
            desc: 'Regístrate y conviértete en miembro participante del ecosistema UMYO Español.'
          },
          {
            number: 2,
            title: 'MANTENTE ACTIVO',
            desc: 'Conecta, participa, apoya negocios, invita a otros y ayuda a fortalecer la comunidad.'
          },
          {
            number: 3,
            title: 'CRECEMOS JUNTOS',
            desc: 'A medida que aumentan la participación de los miembros y los ingresos del ecosistema, nuestra capacidad para financiar la asistencia comunitaria puede aumentar.'
          },
          {
            number: 4,
            title: 'SOLICITA CUANDO LOS PROGRAMAS ESTÉN DISPONIBLES',
            desc: 'Los miembros participantes elegibles pueden solicitar los programas de dificultades disponibles según los requisitos establecidos para cada programa.'
          },
          {
            number: 5,
            title: 'EL APOYO COMUNITARIO SE DISTRIBUYE',
            desc: 'La asistencia aprobada se proporciona según los fondos disponibles, los requisitos de elegibilidad, las reglas del programa y los límites aplicables.'
          }
        ]
      },
      grow: {
        title: 'CUANDO CRECEMOS, DAMOS.',
        desc: 'Cada registro nos ayuda a hacer más por aquellos que más lo necesitan.'
      },
      back: {
        title: 'HASTA UN 20% DE VUELTA.',
        subtitle: 'NUESTRA COMUNIDAD AYUDA A CONSTRUIR EL ECOSISTEMA. EL ECOSISTEMA AYUDA A APOYAR A NUESTRA COMUNIDAD.',
        desc: 'Estamos construyendo tecnología con un propósito más amplio. Nuestro objetivo es crear un ecosistema donde la participación comunitaria pueda ayudar a generar recursos que apoyen a nuestra gente, fortalezcan negocios, creen oportunidades y marquen una diferencia real en la vida cotidiana.',
        list: [
          'NUESTROS MIEMBROS CONSTRUYEN LA COMUNIDAD.',
          'NUESTROS NEGOCIOS AYUDAN A IMPULSAR LA ECONOMÍA.',
          'NUESTRA PARTICIPACIÓN AYUDA A CREAR VALOR.',
          'NUESTRO CRECIMIENTO AYUDA A CREAR MÁS OPORTUNIDADES PARA DEVOLVER.'
        ]
      },
      impact: {
        title: 'CUANTO MÁS CRECEMOS, MÁS IMPACTO PODEMOS CREAR.',
        items: [
          { title: 'MÁS MIEMBROS.', desc: 'Más personas conectadas dentro de nuestra comunidad digital.' },
          { title: 'MÁS NEGOCIOS.', desc: 'Más oportunidades para apoyar negocios y circular actividad económica.' },
          { title: 'MÁS PARTICIPACIÓN.', desc: 'Un ecosistema más fuerte y comprometido.' },
          { title: 'MÁS IMPACTO.', desc: 'Mayor potencial para crear programas y recursos que beneficien a nuestra comunidad.' }
        ],
        tagline: 'CONECTA. APOYA. CRECE. DEVUELVE.'
      },
      built: {
        title: 'CONSTRUIDO PARA NUESTRA COMUNIDAD.',
        desc: 'Esto no se trata simplemente de crear otro sitio web o directorio. Estamos construyendo un ecosistema digital que conecta personas, negocios, creadores, profesionales, organizaciones, tecnología, recursos y oportunidades dentro de una comunidad en crecimiento.',
        emphasis: 'PARA NOSOTROS. POR NOSOTROS.',
        closing: 'Cuando nuestra comunidad participa, nuestra comunidad se vuelve más fuerte.'
      },
      disclaimer: {
        title: 'INFORMACIÓN IMPORTANTE DEL PROGRAMA',
        points: [
          'Los incentivos por dificultades no son pagos en efectivo garantizados ni beneficios garantizados para los miembros.',
          'Los programas están sujetos a requisitos de elegibilidad, fondos disponibles, participación de los miembros, reglas del programa, requisitos de verificación, disponibilidad geográfica y leyes y regulaciones aplicables.',
          'Los montos mostrados representan montos máximos de asistencia potencial para programas calificados. El registro o la membresía no garantizan la aprobación o recepción de asistencia.',
          'Las categorías de programas, montos, requisitos de elegibilidad, períodos de solicitud y disponibilidad pueden cambiar a medida que el ecosistema se desarrolla y crece.'
        ],
        link: 'VER ELEGIBILIDAD Y DETALLES DEL PROGRAMA'
      },
      cta: {
        lead: 'TU MEMBRESÍA AYUDA A CONSTRUIR EL ECOSISTEMA. Cada registro ayuda a fortalecer el ecosistema digital UMYO Español, crear oportunidades, apoyar negocios, expandir tecnología y aumentar nuestro potencial para hacer un impacto significativo en toda nuestra comunidad.',
        title: 'ESTO ES MÁS GRANDE QUE UN DIRECTORIO.',
        subtitle: 'ESTAMOS CONSTRUYENDO TECNOLOGÍA CON PROPÓSITO.',
        cta: 'ÚNETE HOY. CONECTA HOY. CRECE CON NOSOTROS.',
        button: 'REGISTRARSE AHORA'
      },
      footer: {
        tagline: 'PARA NOSOTROS. POR NOSOTROS. POTENCIADO POR UMYO.',
        quickLinks: 'ENLACES RÁPIDOS',
        resources: 'RECURSOS',
        connect: 'CONECTEMOS',
        copyright: '© 2026 Directorio UMYO Español. Todos los derechos reservados.'
      }
    }
  };

  // Incentive cards data with image paths from assets
  incentives = [
    {
      title: 'GAS ASSISTANCE',
      description: 'Helping participating members with transportation expenses for work, school, appointments, and everyday needs.',
      amount: 'UP TO $50',
      icon: 'assets/images/gas-icon.png',
      illustration: 'assets/images/gas-illustration.jpeg'
    },
    {
      title: 'FOOD ASSISTANCE',
      description: 'Grocery assistance designed to help eligible individuals and families with everyday food expenses.',
      amount: 'UP TO $200',
      icon: 'assets/images/food-icon.png',
      illustration: 'assets/images/food-illustration.jpeg'
    },
    {
      title: 'VISA CARD ASSISTANCE',
      description: 'Eligible participating members may have opportunities to receive prepaid assistance for qualifying everyday needs.',
      amount: 'UP TO $200',
      icon: 'assets/images/visa-icon.png',
      illustration: 'assets/images/visa-illustration.jpeg'
    },
    {
      title: 'ELECTRICITY ASSISTANCE',
      description: 'Assistance toward qualifying household electricity expenses.',
      amount: 'UP TO $150',
      icon: 'assets/images/electricity-icon.png',
      illustration: 'assets/images/electricity-illustration.jpeg'
    },
    {
      title: 'CAR NOTE ASSISTANCE',
      description: 'Helping eligible participating members with qualifying vehicle payment needs.',
      amount: 'UP TO $500',
      icon: 'assets/images/car-icon.png',
      illustration: 'assets/images/car-illustration.jpeg'
    },
    {
      title: 'RENT OR MORTGAGE ASSISTANCE',
      description: 'Housing assistance designed to help eligible members facing qualifying financial hardship.',
      amount: 'UP TO $1,000',
      icon: 'assets/images/home-icon.png',
      illustration: 'assets/images/home-illustration.jpeg'
    },
    {
      title: 'ELDERLY ASSISTANCE',
      description: 'Additional assistance opportunities designed to support qualifying elderly members and households.',
      amount: 'UP TO $500',
      icon: 'assets/images/elderly-icon.png',
      illustration: 'assets/images/elderly-illustration.jpeg'
    },
    {
      title: 'MILITARY & VETERAN ASSISTANCE',
      description: 'Support opportunities designed for qualifying military members, veterans, and their families.',
      amount: 'UP TO $500',
      icon: 'assets/images/military-icon.png',
      illustration: 'assets/images/military-illustration.jpeg'
    },
    {
      title: 'SINGLE PARENT ASSISTANCE',
      description: 'Assistance opportunities for qualifying single parent households experiencing financial hardship.',
      amount: 'UP TO $500',
      icon: 'assets/images/singleparent-icon.png',
      illustration: 'assets/images/singleparent-illustration.jpeg'
    },
    {
      title: 'DAY CARE ASSISTANCE',
      description: 'Helping qualifying families with eligible child care expenses.',
      amount: 'UP TO $400',
      icon: 'assets/images/daycare-icon.png',
      illustration: 'assets/images/daycare-illustration.jpeg'
    },
    {
      title: 'EMERGENCY ASSISTANCE',
      description: 'Support for qualifying unexpected circumstances where additional assistance can make a meaningful difference.',
      amount: 'UP TO $1,000',
      icon: 'assets/images/emergency-icon.png',
      illustration: 'assets/images/emergency-illustration.jpeg'
    }
  ];

  // How It Works steps with icons from assets
  steps = [
    {
      number: 1,
      title: 'JOIN THE COMMUNITY',
      description: 'Register and become a participating member of the UMYO Español ecosystem.',
      icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="7" r="4"/><path d="M4 21v-1a6 6 0 0 1 12 0v1"/><path d="M19 14v6"/><path d="M16 17h6"/></svg>`
    },
    {
      number: 2,
      title: 'STAY ACTIVE',
      description: 'Connect, participate, support businesses, invite others, and help strengthen the community.',
      icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="8" r="3"/><circle cx="16" cy="8" r="3"/><path d="M2 20v-1a5 5 0 0 1 10 0v1"/><path d="M12 20v-1a5 5 0 0 1 10 0v1"/></svg>`
    },
    {
      number: 3,
      title: 'WE GROW TOGETHER',
      description: 'As membership participation and ecosystem revenue increase, our ability to fund community assistance can increase.',
      icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20.8 8.8a5 5 0 0 0-8.8-3.2 5 5 0 0 0-8.8 3.2c0 5 8.8 10 8.8 10s8.8-5 8.8-10Z"/><path d="M12 7v5"/><path d="M9.5 9.5 12 12l2.5-2.5"/></svg>`
    },
    {
      number: 4,
      title: 'APPLY WHEN PROGRAMS ARE AVAILABLE',
      description: 'Eligible participating members can apply for available hardship programs based on the requirements established for each program.',
      icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/><path d="M14 2v6h6"/><path d="m9 15 2 2 4-4"/></svg>`
    },
    {
      number: 5,
      title: 'COMMUNITY SUPPORT IS DISTRIBUTED',
      description: 'Approved assistance is provided according to available funding, eligibility requirements, program rules, and applicable limits.',
      icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`
    }
  ];

  constructor(private sanitizer: DomSanitizer) {}

  safeSvg(svg: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  }
}